import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { OptionKey } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { Pagination, PaginationParams } from '@/common/types';
import { API_URL } from '@/dtos';

import { IPermission, RoleLevel } from '../role.types';
import { RolesListItemPayload } from '../RolesList/RolesList.types';

export const QUERY_KEY = 'GET_ROLES_LIST';

interface RolesListItemResponse {
  id: number;
  description: string;
  level_type: RoleLevel;
  name: string;
  organization_id: number;
  organization_name: string;
}

export interface RolesListSearchParams extends PaginationParams {
  partnerIds: OptionKey[];
  permissions: OptionKey[];
  levelType: RoleLevel;
  enabled?: boolean;
  currentRoleId?: string;
}

const useRolesListQuery = ({
  page,
  perPage,
  permissions,
  searchString,
  partnerIds,
  enabled = true,
  sortBy,
  levelType,
  currentRoleId,
}: RolesListSearchParams) => {
  const { isError, isSuccess, data, isLoading } = useQuery(
    [QUERY_KEY, page, perPage, searchString, sortBy, enabled, levelType, ...partnerIds, ...permissions],
    () =>
      // TODO: the type is missing
      api.get<Pagination<RolesListItemResponse>>(API_URL.ROLES, {
        params: _.pickBy({
          page: page - 1,
          size: perPage,
          sort: `name,${sortBy}`,
          search_value: searchString || '',
          level_type: levelType === RoleLevel.All ? undefined : levelType,
          permission_ids: permissions.join(','),
          organization_ids: partnerIds.join(','),
        }),
      }),
    {
      retry: 0,
      enabled,
    }
  );

  const currentData = useMemo<RolesListItemPayload[]>(() => {
    const list = data?.data?.content || [];
    return list.map((item) => ({
      id: `${item.id}`,
      name: item.name,
      description: item.description,
      organizationId: `${item.organization_id}`,
      level: item.level_type,
      organizationName: item.organization_name,
    }));
  }, [data?.data?.content]);

  const permissionOptions: IPermission[] = useMemo(() => {
    const list = data?.data?.content || [];
    const formattedList: IPermission[] = list.map((item) => {
      const currentId = `${item.id}`;
      return {
        id: currentId,
        key: currentId,
        name: `${item.name} ${currentId === currentRoleId ? '(this role)' : ''}`,
        description: item.description,
        group: levelType,
      };
    });
    return formattedList;
  }, [currentRoleId, data?.data?.content, levelType]);

  const total = useMemo(() => data?.data?.totalElements || null, [data?.data.totalElements]);

  return {
    permissionOptions,
    total,
    data: currentData,
    isLoading,
    isSuccess,
    isError,
  };
};

export default useRolesListQuery;
